import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { assetTypeValues } from "../../../constants";

export const DEBT_ENCUMBRANCE_BREAKDOWN_COLUMNS = [
  { key: "lease_ref_code", label: "Lease Ref Code" },
  { key: "lessee", label: "Lessee" },
  { key: "asset_label", label: "Asset" },
  { key: "loan", label: "Loan" },
  { key: "status", label: "Status", type: "pill" },
];

// Same tones the backend uses for the main table's status pills.
const STATUS_TONE = { active: "green", closed: "neutral" };

function buildDebtEncumbranceRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: `${item?.asset_sn || "asset"}-${item?.loan || "loan"}-${index}`,
    asset_sn: item?.asset_sn || "",
    lease_ref_code: item?.lease_ref_code || "-",
    lessee: item?.lessee || "-",
    asset_label: [assetTypeValues[item?.asset_type]?.label, item?.asset_sn].filter(Boolean).join(" ") || "-",
    loan: item?.loan || "-",
    status: { label: item?.status || "-", tone: STATUS_TONE[String(item?.status || "").toLowerCase()] || "navy" },
  }));
}

// Table rows show the asset as "MSN <sn>"; strip the prefix to match the breakdown's asset_sn.
function assetSnFromTableRow(row) {
  const asset = typeof row?.asset === "string" ? row.asset : "";
  return asset.replace(/^MSN\s+/i, "");
}

// Clicking a table row opens debt_encumbrance_breakdown filtered to that row's asset (all its loans).
export function useDebtEncumbranceExposureBreakdown({ debtBreakdown, onTrack }) {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const reset = useCallback(() => setSelectedAsset(null), []);

  const allRows = useMemo(() => buildDebtEncumbranceRows(debtBreakdown), [debtBreakdown]);
  const rows = useMemo(
    () => (selectedAsset ? allRows.filter((row) => row.asset_sn === selectedAsset.sn) : []),
    [allRows, selectedAsset],
  );

  const onRowClick = useCallback((row) => {
    const sn = assetSnFromTableRow(row);
    if (!sn) return;
    setSelectedAsset({ sn, label: row.asset });
    onTrack?.(`Graph Row Clicked: ${row.asset}`);
  }, [onTrack]);

  return { rows, canRender: allRows.length > 0, onRowClick, modalOpen: !!selectedAsset, closeModal: reset, titleSuffix: selectedAsset?.label || "", reset };
}

export function DebtEncumbranceExposureBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Debt & Encumbrance Exposure";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${rowCount} ${rowCount === 1 ? "Loan" : "Loans"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={DEBT_ENCUMBRANCE_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
